import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { BankAccount, PaymentMethod, StripePaymentMethod, WirePaymentMethod } from 'types/payment';
import {
	CheckoutAccountFormStatus,
	CheckoutAutoInvestDetails,
	CheckoutOrderGroupDetails,
	CheckoutType,
	EntityAccountDetails,
	JointAccountDetails,
	PaymentMethodCreationMethod,
	PaymentMethodSetup,
	PlanSelectionPayload,
	ServiceLevelUpdatePayload,
	TrustAccountDetails
} from 'types/checkout';
import {
	completeGiftedCheckout,
	createOrderGroup,
	getSignupCheckoutSelections,
	isEligibleForAdditionalInvestment,
	placeOrder,
	submitNewEntityAccountDetails,
	submitNewJointAccountDetails,
	submitNewTrustAccountDetails,
	updateAutoInvest,
	updateExistingEntityAccountDetails,
	updateExistingJointAccountDetails,
	updateExistingTrustAccountDetails,
	updateInvestmentSelections,
	updateSignUpCheckoutType
} from '@api/checkout';
import { Country, K1Acknowledgement } from 'types/form';
import { DripStatus, InvestmentEntity, InvestmentEntityType } from 'types/investment-entity';
import { getOrderFormDetails, setUserCountry } from '@api/user';
import { accountDetailsRoute } from '@src/utils/checkout';
import { app } from './app';
import { attachPaymentMethod } from '@api/bank-accounts';
import { getRiaPlanByStrategyAndType } from '@api/ria';
import { investmentEntity } from '@store/modules/investment-entity';
import { isStripePaymentMethod } from '@utils/payment-methods';
import { Link } from 'types/layout';
import { RiaPlan } from 'types/plan';
import { ServiceLevelName } from 'types/service-level';
import store from '..';
import { trackMixpanel } from '@utils/analytics';
import { user } from '@store/modules/user';
import { UserCheckoutDetails } from 'types/user';

@Module({
	dynamic: true,
	namespaced: true,
	name: 'orderForm',
	store
})
class OrderFormModule extends VuexModule {
	dataRetrieved = false;
	plan: RiaPlan | null = null;
	serviceLevel: ServiceLevelName | null = null;
	acknowledgedK1Disclosure: K1Acknowledgement = { acknowledged: false };
	investmentAmount: number | null = null;
	citizenshipCountry: Country | null = null;
	residenceCountry: Country | null = null;
	hasSubmittedSsn = false;
	hasSubmittedDob = false;
	hasSubmittedAccountDetails = false;
	autoInvestDetails: CheckoutAutoInvestDetails | null = null;
	signUpCheckoutType: CheckoutType | null = null;
	orderGroupId: string | null = null;
	paymentMethod: PaymentMethod | null = null;
	paymentMethodSetup: PaymentMethodSetup | null = null;
	paymentMethodCreationMethod: PaymentMethodCreationMethod | null = null;
	investments: Array<string> | null = null;
	reinvestmentPreference: DripStatus | null = null;
	acknowledgmentsDocIds: Array<string> | null = null;
	isUnclaimedGiftedAccount = false;
	isQuickInvest = false;

	get countriesSubmitted(): boolean {
		return !!(this.citizenshipCountry && this.residenceCountry);
	}

	get residenceValid(): boolean {
		return this.residenceCountry?.code === 'US';
	}

	get isStripePayment(): boolean {
		return this.paymentMethod ? isStripePaymentMethod(this.paymentMethod) : false;
	}

	@Action({ rawError: true })
	public async getUserCheckoutDetailsData(): Promise<void> {
		const allDetails = await getOrderFormDetails();

		const data: UserCheckoutDetails = allDetails.details;
		const countries: Array<Country> = allDetails.countries;
		const selections = await getSignupCheckoutSelections();
		this.UPDATE_IS_UNCLAIMED_GIFTED_ACCOUNT(selections.giftedAccount);
		this.UPDATE_IS_QUICK_INVEST(selections.quickInvest);

		trackMixpanel('Plan and Service Level Selections Fetched', {
			planId: data.plan?.id,
			planName: data.plan?.name,
			serviceLevel: data.serviceLevel
		});

		if (!this.plan) {
			this.UPDATE_PLAN(data.plan);
		}
		if (!this.serviceLevel) {
			this.UPDATE_SERVICE_LEVEL(data.serviceLevel);
		}
		if (data.citizenshipCountryCode) {
			const citizenshipCountry = countries.filter((country) => country.code === data.citizenshipCountryCode)[0];
			this.UPDATE_CITIZENSHIP_COUNTRY(citizenshipCountry);
		}
		if (data.residenceCountryCode) {
			const residenceCountry = countries.filter((country) => country.code === data.residenceCountryCode)[0];
			this.UPDATE_RESIDENCE_COUNTRY(residenceCountry);
		}
		this.UPDATE_HAS_SUBMITTED_SSN(data.hasSubmittedSsn);
		this.UPDATE_HAS_SUBMITTED_DOB(data.hasSubmittedDob);
		this.UPDATE_HAS_SUBMITTED_ACCOUNT_DETAILS(data.hasSubmittedAccountDetails);
		this.UPDATE_ORDER_GROUP_ID(data.orderGroupId);
		this.UPDATE_SIGN_UP_CHECKOUT_TYPE(data.signUpCheckoutType || null);
		if (data.investmentAmount) {
			this.UPDATE_INVESTMENT_AMOUNT(parseInt(data.investmentAmount));
		}
		if (data.autoInvestSelections) {
			if (data.autoInvestSelections.recurringAmount) {
				data.autoInvestSelections.recurringAmount = parseInt(
					data.autoInvestSelections.recurringAmount as string
				);
			}
			this.UPDATE_AUTO_INVEST_DETAILS(data.autoInvestSelections);
		}
		if (data.paymentMethod) {
			if (data.paymentMethod.paymentMethodType === 'STRIPE') {
				this.UPDATE_PAYMENT_METHOD(data.paymentMethod.stripePayment);
			} else {
				this.UPDATE_PAYMENT_METHOD(data.paymentMethod.bankAccount);
			}
		}
		if (data.paymentMethodSetup) {
			this.UPDATE_PAYMENT_METHOD_SETUP(data.paymentMethodSetup);
		}
		if (data.paymentMethodCreationMethod) {
			this.UPDATE_PAYMENT_METHOD_CREATION_METHOD(data.paymentMethodCreationMethod);
		}
		if (data.investments) {
			this.UPDATE_INVESTMENTS(data.investments);
		}
		if (data.reinvestmentPreference) {
			this.UPDATE_REINVESTMENT_PREFERENCE(data.reinvestmentPreference);
		}
		this.UPDATE_DATA_RETRIEVED(true);
	}

	@Action({ rawError: true })
	public async submitPlanSelection(planSelectionPayload: PlanSelectionPayload): Promise<string> {
		const { serviceLevelName: serviceLevel, investmentStrategy, planType } = planSelectionPayload;
		const plan: RiaPlan = await getRiaPlanByStrategyAndType(investmentStrategy, planType);

		if (serviceLevel) {
			this.UPDATE_SERVICE_LEVEL(serviceLevel);
		}

		this.UPDATE_PLAN(plan);

		await updateInvestmentSelections(plan, this.serviceLevel);

		return 'checkout-start';
	}

	@Action({ rawError: true })
	public async updateServiceLevel(serviceLevelUpdatePayload: ServiceLevelUpdatePayload): Promise<string> {
		const { serviceLevelName, isQuestionnaireContext } = serviceLevelUpdatePayload;
		switch (serviceLevelName) {
			case 'STARTER': {
				const name = await this.submitPlanSelection({
					investmentStrategy: 'STARTER_PORTFOLIO',
					planType: 'STARTER',
					serviceLevelName
				});
				return name;
			}
			case 'BASIC': {
				const name = await this.submitPlanSelection({
					investmentStrategy: 'BASIC_PORTFOLIO',
					planType: 'BASIC',
					serviceLevelName
				});
				return name;
			}
			case 'CORE':
			case 'ADVANCED':
			case 'PREMIUM':
				this.UPDATE_SERVICE_LEVEL(serviceLevelName);
				return isQuestionnaireContext ? 'results-select-core-plan' : 'core-plan';
			default:
				return '404';
		}
	}

	@Action({ rawError: true })
	public async submitUserCountrySelections(payload: {
		citizenshipCountry: Country;
		residenceCountry: Country;
		isIRACheckout: boolean;
	}): Promise<string> {
		const countryCode = payload.residenceCountry.code;
		await setUserCountry({
			citizenshipCountryCode: payload.citizenshipCountry.code,
			residenceCountryCode: countryCode
		});
		this.UPDATE_CITIZENSHIP_COUNTRY(payload.citizenshipCountry);
		this.UPDATE_RESIDENCE_COUNTRY(payload.residenceCountry);
		if (countryCode === 'US') {
			if (payload.isIRACheckout) {
				return 'ira-checkout-individual-details';
			} else {
				if (this.isUnclaimedGiftedAccount) {
					return 'checkout-individual-account-details';
				} else {
					return 'checkout-account-type';
				}
			}
		} else {
			return 'checkout-country-not-supported';
		}
	}

	@Action({ rawError: true })
	public async updatePaymentMethod(accountDetails: {
		newAccount: BankAccount | StripePaymentMethod | WirePaymentMethod;
		setup: PaymentMethodSetup;
		creationMethod: PaymentMethodCreationMethod;
	}): Promise<string> {
		const transactionType = isStripePaymentMethod(accountDetails.newAccount) ? 'STRIPE' : 'ACH';
		await attachPaymentMethod(this.orderGroupId ?? '', accountDetails.newAccount.id, transactionType);
		this.UPDATE_PAYMENT_METHOD(accountDetails.newAccount);
		this.UPDATE_PAYMENT_METHOD_SETUP(accountDetails.setup);
		this.UPDATE_PAYMENT_METHOD_CREATION_METHOD(accountDetails.creationMethod);
		return 'checkout-acknowledgments';
	}

	@Action
	public async submitSignupCheckoutType(checkoutType: CheckoutType): Promise<void> {
		if (checkoutType !== this.signUpCheckoutType) {
			this.UPDATE_DATA_RETRIEVED(false);
		}
		if (!user.isExistingInvestor) {
			await updateSignUpCheckoutType(checkoutType);
		}
		this.UPDATE_SIGN_UP_CHECKOUT_TYPE(checkoutType);
	}

	@Action({ rawError: true })
	public async submitIraCheckout(): Promise<string> {
		await this.submitSignupCheckoutType('IRA');
		if (user.user) {
			if (!this.dataRetrieved) {
				await this.getUserCheckoutDetailsData();
			}
			if (user.hasFirstLastName && this.countriesSubmitted && this.residenceValid) {
				if (user.hasIraEntity) {
					return 'ira-start';
				} else {
					return 'ira-checkout-individual-details';
				}
			}
		}
		return 'checkout-start';
	}

	@Action({ rawError: true })
	public async submitSelectedAccount(investmentEntity: InvestmentEntity): Promise<void> {
		const { investmentEntityId, entityType } = investmentEntity;
		const isRepeatInvestor = await isEligibleForAdditionalInvestment(investmentEntityId);
		const nextRoute = isRepeatInvestor ? { name: 'add-funds-ria' } : accountDetailsRoute(entityType, 'update');
		await user.updateSelectedEntityWithoutReload({
			investmentEntityId: investmentEntityId,
			nextRoute: { route: nextRoute }
		});
	}

	@Action({ rawError: true })
	public async submitAccountDetails(submissionDetails: {
		investmentType: InvestmentEntityType;
		userRoute: CheckoutAccountFormStatus;
		payload: JointAccountDetails | TrustAccountDetails | EntityAccountDetails;
		settingsEntityCreation?: boolean;
	}): Promise<string> {
		if (submissionDetails.userRoute === 'update') {
			switch (submissionDetails.investmentType) {
				case 'JOINT_ACCOUNT':
					await updateExistingJointAccountDetails(submissionDetails.payload as JointAccountDetails);
					break;
				case 'LLC':
					await updateExistingEntityAccountDetails(submissionDetails.payload as EntityAccountDetails);
					break;
				case 'TRUST':
					await updateExistingTrustAccountDetails(submissionDetails.payload as TrustAccountDetails);
					break;
			}
		} else {
			let investmentEntityId;
			switch (submissionDetails.investmentType) {
				case 'JOINT_ACCOUNT':
					investmentEntityId = (
						await submitNewJointAccountDetails(submissionDetails.payload as JointAccountDetails)
					).investmentEntityId;
					break;
				case 'LLC':
					investmentEntityId = (
						await submitNewEntityAccountDetails(submissionDetails.payload as EntityAccountDetails)
					).investmentEntityId;
					break;
				case 'TRUST':
					investmentEntityId = (
						await submitNewTrustAccountDetails(submissionDetails.payload as TrustAccountDetails)
					).investmentEntityId;
					break;
			}

			if (investmentEntityId) {
				this.UPDATE_HAS_SUBMITTED_SSN(true);
				this.UPDATE_HAS_SUBMITTED_DOB(true);
				this.UPDATE_HAS_SUBMITTED_ACCOUNT_DETAILS(true);

				if (!submissionDetails.settingsEntityCreation) {
					await user.updateSelectedEntityWithoutReload({
						investmentEntityId,
						nextRoute: { route: { name: 'checkout-payment-amount' } }
					});
				} else {
					return investmentEntityId ?? '';
				}
			}
		}

		this.UPDATE_HAS_SUBMITTED_SSN(true);
		this.UPDATE_HAS_SUBMITTED_DOB(true);
		this.UPDATE_HAS_SUBMITTED_ACCOUNT_DETAILS(true);

		return 'checkout-payment-amount';
	}

	@Action({ rawError: true })
	public async submitOrderForm(): Promise<string> {
		if (this.plan) {
			await updateInvestmentSelections(this.plan, this.serviceLevel);
		}

		return 'checkout-select-country';
	}

	@Action({ rawError: true })
	public async placeOrder(): Promise<Link> {
		if (orderForm.isUnclaimedGiftedAccount) {
			await completeGiftedCheckout();
		} else {
			await placeOrder(
				this.orderGroupId as string,
				this.acknowledgmentsDocIds as Array<string>,
				this.reinvestmentPreference,
				this.autoInvestDetails
			);
		}

		if (app.isVerified) {
			await user.getUser();
			await investmentEntity.refreshPositionsAndPerformance();
			return { router: 'reits-checkout-success' };
		} else {
			return { router: 'checkout-verify-email' };
		}
	}

	@Action({ rawError: true })
	public async submitOrderGroup(initialContribution: string): Promise<string> {
		this.UPDATE_PAYMENT_METHOD(null);
		const orderGroupPayload: CheckoutOrderGroupDetails = {
			amount: initialContribution,
			riaPlanId: this.plan?.id || '',
			serviceLevelUpgrade: this.serviceLevel
		};
		const orderGroupResponse = await createOrderGroup(orderGroupPayload);
		const orderGroupId = orderGroupResponse?.orderGroupId ?? '';
		this.UPDATE_ORDER_GROUP_ID(orderGroupId);
		return orderGroupId;
	}

	@Action({ rawError: true })
	public async submitAutoInvestSelections(selections: {
		orderGroupId: string;
		addAutoInvest: boolean;
		autoInvestAmount: string;
		frequency: string;
		isMonthlyFrequency: boolean;
		firstInvestmentDay: string;
		dayOfWeek: string;
		startDate: string;
	}): Promise<string> {
		const autoInvestSelections: CheckoutAutoInvestDetails = {
			reitInvestmentOrderGroupId: selections.orderGroupId,
			addRecurringInvestmentSchedule: selections.addAutoInvest
		};
		if (selections.addAutoInvest) {
			autoInvestSelections.recurringAmount = selections.autoInvestAmount as string;
			autoInvestSelections.frequency = selections.frequency;

			if (selections.isMonthlyFrequency) {
				autoInvestSelections.firstInvestmentDayOfMonth = selections.firstInvestmentDay;
			} else {
				autoInvestSelections.investmentDayOfWeek = selections.dayOfWeek;
				autoInvestSelections.startDate = selections.startDate;
			}
		}
		await updateAutoInvest(autoInvestSelections);
		return 'checkout-payment-method';
	}

	@Mutation
	public UPDATE_DATA_RETRIEVED(state: boolean): void {
		this.dataRetrieved = state;
	}

	@Mutation
	public UPDATE_PLAN(plan: RiaPlan | null): void {
		this.plan = plan;
	}

	@Mutation
	public UPDATE_SERVICE_LEVEL(serviceLevel: ServiceLevelName | null): void {
		this.serviceLevel = serviceLevel;
	}

	@Mutation
	public UPDATE_ACKNOWLEDGED_K1_DISCLOSURE(acknowledgedK1Disclosure: K1Acknowledgement): void {
		this.acknowledgedK1Disclosure = acknowledgedK1Disclosure;
	}

	@Mutation
	public UPDATE_INVESTMENT_AMOUNT(amount: number | null): void {
		this.investmentAmount = amount;
	}

	@Mutation
	public UPDATE_CITIZENSHIP_COUNTRY(citizenshipCountry: Country): void {
		this.citizenshipCountry = citizenshipCountry;
	}

	@Mutation
	public UPDATE_RESIDENCE_COUNTRY(residenceCountry: Country): void {
		this.residenceCountry = residenceCountry;
	}

	@Mutation
	public UPDATE_HAS_SUBMITTED_SSN(hasSubmittedSsn: boolean): void {
		this.hasSubmittedSsn = hasSubmittedSsn;
	}

	@Mutation
	public UPDATE_HAS_SUBMITTED_DOB(hasSubmittedDob: boolean): void {
		this.hasSubmittedDob = hasSubmittedDob;
	}

	@Mutation
	public UPDATE_HAS_SUBMITTED_ACCOUNT_DETAILS(hasSubmittedAccountDetails: boolean): void {
		this.hasSubmittedAccountDetails = hasSubmittedAccountDetails;
	}

	@Mutation
	public UPDATE_AUTO_INVEST_DETAILS(autoInvestDetails: CheckoutAutoInvestDetails): void {
		this.autoInvestDetails = autoInvestDetails;
	}

	@Mutation
	public UPDATE_ORDER_GROUP_ID(id: string): void {
		this.orderGroupId = id;
	}

	@Mutation
	public UPDATE_SIGN_UP_CHECKOUT_TYPE(checkoutType: CheckoutType | null): void {
		this.signUpCheckoutType = checkoutType;
	}

	@Mutation
	public UPDATE_PAYMENT_METHOD(paymentMethod: PaymentMethod | null): void {
		this.paymentMethod = paymentMethod;
	}

	@Mutation
	public UPDATE_PAYMENT_METHOD_SETUP(paymentMethodSetup: PaymentMethodSetup): void {
		this.paymentMethodSetup = paymentMethodSetup;
	}

	@Mutation
	public UPDATE_PAYMENT_METHOD_CREATION_METHOD(paymentMethodCreationMethod: PaymentMethodCreationMethod): void {
		this.paymentMethodCreationMethod = paymentMethodCreationMethod;
	}

	@Mutation
	public UPDATE_INVESTMENTS(investments: Array<string>): void {
		this.investments = investments;
	}

	@Mutation
	public UPDATE_REINVESTMENT_PREFERENCE(reinvestmentPreference: DripStatus): void {
		this.reinvestmentPreference = reinvestmentPreference;
	}

	@Mutation
	public UPDATE_ACKNOWLEDGMENTS_DOC_IDS(acknowledgmentDocIds: Array<string>): void {
		this.acknowledgmentsDocIds = acknowledgmentDocIds;
	}

	@Mutation
	public UPDATE_IS_QUICK_INVEST(isQuickInvest: boolean): void {
		this.isQuickInvest = isQuickInvest;
	}

	@Mutation
	public UPDATE_IS_UNCLAIMED_GIFTED_ACCOUNT(isUnclaimedGiftedAccount: boolean): void {
		this.isUnclaimedGiftedAccount = isUnclaimedGiftedAccount;
	}
}

export const orderForm = getModule(OrderFormModule);
